import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import {
  canDecide,
  isAssignedToUser,
  isFlaggedWork,
  isVisibleKind,
  makeIsSurplusSlot,
  makeIsLive,
} from '@/lib/taskRules'
import type { Task } from '@/api/types'

/**
 * The task buckets Home counts, derived with the **same predicates `/work`
 * uses** — `isVisibleKind` → `makeIsLive` → status/ownership.
 *
 * That equality is the point. The old dashboard counted global task state, so a
 * technician's "In progress" included other people's work and none of the four
 * numbers matched the queue they linked to. Anything me-scoped here goes
 * through `isAssignedToUser(task, me.user_id)`.
 *
 * Two other rules:
 * - Subscribes to the canonical `['tasks']` key, so this costs zero extra polls
 *   (the notification bell already refetches it every 30s app-wide) and can't
 *   drift from `/work` or `/activity`.
 * - `enabled` on `task:read`. `developer` does not hold it, and a 403 every poll
 *   would raise `App.tsx`'s global destructive toast — a bug, not noise.
 */
export function useTaskBuckets() {
  const { me, can } = useAuth()
  const canReadTasks = can('task:read')

  const query = useQuery<Task[]>({
    queryKey: ['tasks'],
    queryFn: () => apiClient.fetchTasks(),
    enabled: !!me && canReadTasks,
  })

  const userId = me?.user_id ?? ''
  const role = me?.role ?? 'technician'
  const canReview = can('task:review')
  // Maintainer/admin bypass the gateway's ownership check in `review_task`.
  const ownershipExempt = role === 'admin' || role === 'maintainer'

  const all = query.data ?? []
  const visible = all.filter((t) => isVisibleKind(t, { role, canReview }))
  const isLive = makeIsLive(visible)
  // Same rule the Available tab uses: one card per step, never a slot that is
  // somebody else's or a duplicate of the one already shown.
  const isSurplusSlot = makeIsSurplusSlot(visible, userId)
  const mine = (t: Task) => isAssignedToUser(t, userId)

  const inProgress = visible.filter(
    (t) =>
      mine(t) &&
      isLive(t) &&
      t.status !== 'submitted' &&
      t.status !== 'complete' &&
      t.status !== 'under_review',
  )

  return {
    // `enabled: false` reports `isLoading` forever in v5 — a role without
    // task:read has no tasks pending, it has none at all.
    isLoading: canReadTasks && query.isLoading,
    isError: query.isError,
    /** Unfiltered rows — the flagged split needs the same input `/work` passes
     *  to `FlaggedDecisions`, which is `allTasks`, not the kind-filtered list. */
    all,
    inProgress,
    /** Claimed by me and reworked: the reviewer sent it back. */
    rework: inProgress.filter((t) => (t.rejection_count ?? 0) > 0),
    available: visible.filter(
      (t) =>
        (t.status === 'pending' || t.status === 'partially_claimed') &&
        isLive(t) &&
        !isSurplusSlot(t),
    ),
    /** Mine, signed and sent — waiting on someone else. */
    submitted: visible.filter((t) => mine(t) && t.status === 'submitted'),
    /** Flagged rows are rendered by `FlaggedDecisions`, not as plain verdicts. */
    decidable: visible.filter(
      (t) =>
        !isFlaggedWork(t) &&
        canDecide(t, {
          canReview,
          isAssignedToMe: mine(t),
          ownershipExempt,
          isLive: isLive(t),
          currentUserId: userId,
        }),
    ),
    flagged: all.filter(isFlaggedWork),
  }
}
